import { useEffect, useState, useContext } from 'react';
import { isEmpty, omitBy } from 'lodash';
import getConfig from 'next/config';
import logger from '@greenlight/logger';
import { storageAvailable } from '../../../helpers';
import optimizelyClient from '../../../utils/optimizely-fs';
import { useToggleDispatchers } from '../../../state/toggles';
import { setStandardToggle, getFeatureToggles } from '../../../helpers/toggles';

import {
  FEATURES,
  MINIMUM_WALLET_LOAD,
  PROVE_MOBILE_AUTH_ENABLED,
  CREDIT_WAITLIST,
  PREFER_NOT_TO_SAY_OPTION_ENABLED,
  SMS_NOTIFICATIONS_ENABLED,
  ENHANCED_DEBIT
} from '../constants';
import { useApp, useAppDispatchers } from '../../../state/app';
import { getSearchParam } from '../../../helpers/getSearchParam';
import { useUser } from '../../../state/user';
import { getAnonymousId } from '..';
import { AnalyticsContext } from '../../../ModularReduxAnalytics';

const { publicRuntimeConfig } = getConfig();

function getUtmSource() {
  let utmSource;

  // First, check for UTM in user's URL
  const urlParams = new URLSearchParams(window.location.search);
  utmSource = urlParams.get('utm_source');

  if (utmSource) {
    return utmSource;
  }

  // If it's not in the URL, check in local storage
  if (!utmSource && storageAvailable('localStorage')) {
    const adsSuperPropertiesFromLocalStorage =
      localStorage.getItem('adsSuperProperties');
    const adsSuperProperties = adsSuperPropertiesFromLocalStorage
      ? JSON.parse(adsSuperPropertiesFromLocalStorage)
      : null;
    utmSource = adsSuperProperties ? adsSuperProperties.utm_source : undefined;

    return utmSource;
  }
}

function getWebregInAppsAttributes(appOs, appVersion, embeddedWebreg) {
  return {
    ...omitBy(
      {
        appOs,
        appVersion
      },
      isEmpty
    ),
    embeddedWebreg
  };
}

function getPartnerSource(partner) {
  const partnerSource = getSearchParam('partner') || partner;
  return { ...(partnerSource && { partnerSource }) };
}

function isPartnerPage() {
  return ['/partners', '/partners/'].includes(window.location.pathname);
}

function getProveMobileAuthToggleEligible() {
  return (
    !getSearchParam('phoneNumber') &&
    !getSearchParam('partner') &&
    !isPartnerPage() &&
    !(
      storageAvailable('sessionStorage') &&
      (sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('regAccessToken'))
    )
  );
}

const getStandardToggles = toggleDispatchers => [
  {
    featureName: FEATURES.minimumWalletLoad,
    cookieName: 'minimumWalletLoadVariation',
    segmentPropertyName: 'minimumWalletLoadVariation',
    onVariationCallback: (variationKey, variables, enabled) => {
      if (enabled) {
        toggleDispatchers.setMinimumWalletLoad({
          shouldShift:
            variationKey !== MINIMUM_WALLET_LOAD.experimentOffVariant,
          minimumLoadAmount:
            variables[MINIMUM_WALLET_LOAD.minimumAmountKey] ||
            MINIMUM_WALLET_LOAD.defaultMinimumAmount,
          displayMinimumAmount:
            variables[MINIMUM_WALLET_LOAD.displayMinimumAmountKey]
        });
      }
    }
  },
  {
    // This will be moved to the FundingAccount/FundWallet screens as part of NET-1557
    featureName: FEATURES.hideFunding,
    cookieName: null,
    segmentPropertyName: null,
    onVariationCallback: (variation, variables, enabled) => {
      if (enabled) {
        toggleDispatchers.setHideFunding({
          hidePlaid: variables.hidePlaid,
          hideManualACH: variables.hideManualACH,
          hideDebit: variables.hideDebit
        });
      }
    }
  },
  {
    featureName: FEATURES.emailErrorStateOnBlur,
    cookieName: 'emailErrorStateOnBlurVariation',
    segmentPropertyName: 'emailErrorStateOnBlurVariation',
    onVariationCallback: (variationKey, variables, enabled) => {
      if (enabled) {
        toggleDispatchers.setEmailBlurEnabled();
      }
    }
  },
  {
    featureName: FEATURES.creditWaitlist,
    cookieName: 'creditWaitlistVariation',
    segmentPropertyName: 'creditWaitlistVariation',
    onVariationCallback: (variationKey, variables, enabled) => {
      if (enabled && variationKey !== CREDIT_WAITLIST.experimentOffVariant) {
        toggleDispatchers.setCreditWaitlistEnabled({
          creditWaitlistEnabled: enabled,
          creditWaitlistShowConfirmScreen:
            typeof variables[CREDIT_WAITLIST.showConfirmScreenKey] !==
            'undefined'
              ? variables[CREDIT_WAITLIST.showConfirmScreenKey]
              : CREDIT_WAITLIST.showConfirmScreenDefault
        });
      }
    }
  },
  {
    featureName: FEATURES.preferNotToSayOptionEnabled,
    cookieName: null,
    segmentPropertyName: null,
    onVariationCallback: (variationKey, variables, enabled) => {
      if (
        enabled &&
        variationKey !== PREFER_NOT_TO_SAY_OPTION_ENABLED.experimentOffVariant
      ) {
        toggleDispatchers.setPreferNotToSayOptionEnabled();
      }
    }
  },
  {
    featureName: FEATURES.smsNotificationsEnabled,
    cookieName: 'smsNotificationsVariation',
    segmentPropertyName: 'smsNotificationsVariation',
    onVariationCallback: (variationKey, variables, enabled) => {
      if (
        enabled &&
        variationKey !== SMS_NOTIFICATIONS_ENABLED.experimentOffVariant
      ) {
        toggleDispatchers.setSmsNotificationsEnabled({
          defaultChecked: variables.defaultChecked
        });
      }
    }
  },
  {
    featureName: FEATURES.enhancedDebitEnabled,
    cookieName: 'enhancedDebitVariation',
    segmentPropertyName: 'enhancedDebitVariation',
    onVariationCallback: (variationKey, variables, enabled) => {
      if (enabled) {
        toggleDispatchers.setEnhancedDebitOptions({
          applePayEnabled: variables[ENHANCED_DEBIT.applePayEnabledKey],
          googlePayEnabled: variables[ENHANCED_DEBIT.googlePayEnabledKey],
          threeDSEnabled: variables[ENHANCED_DEBIT.threeDSEnabledKey],
          paypalEnabled: variables[ENHANCED_DEBIT.paypalEnabledKey],
          venmoEnabled: variables[ENHANCED_DEBIT.venmoEnabledKey]
        });
      }
    }
  }
];

const loadFeatures = async ({
  anonymousId,
  attributes,
  setTogglesLoaded,
  toggleDispatchers
}) => {
  let usingDefault = false;

  const setFeaturesToDefault = () => {
    usingDefault = true;

    setTogglesLoaded({ loaded: true });
  };

  const timeout = setTimeout(() => {
    setFeaturesToDefault();
  }, 5000);

  try {
    const initializedClient = optimizelyClient.start();

    const featureToggles = await getFeatureToggles(anonymousId, attributes);
    toggleDispatchers.setFeatureToggles(featureToggles);

    // Get all the toggles without firing any impression events
    // for the time being. Impressions cost money and introduce noise
    // in the data, so only send them if needed!
    const toggles = await initializedClient.getEnabledFeatures(
      { anonymousId },
      attributes,
      Object.values(FEATURES)
    );

    if (!usingDefault) {
      clearTimeout(timeout);
      document.cookie = `globalHoldoutEnabled = ${
        toggles[FEATURES.globalHoldout]
      }`;

      const simpleToggles = {
        [FEATURES.plaidOAuthManualOverride]: () => {
          toggleDispatchers.setPlaidOAuthManualOverride(true);
        },
        [FEATURES.plaidOAuthAndroidLink]: () => {
          toggleDispatchers.setPlaidOAuthAndroidLink(true);
        },
        [FEATURES.nativePlaidFlow]: () => {
          toggleDispatchers.setNativePlaidFlowEnabled();
        },
        [FEATURES.newCardImage]: () => {
          toggleDispatchers.setNewCardImageEnabled();
        },
        [FEATURES.accessManagementEnabled]: () => {
          toggleDispatchers.setAccessManagementEnabled(true);
        },
        [FEATURES.promoCodeVerificationEnabled]: () => {
          toggleDispatchers.setPromoCodeVerificationEnabled(true);
        },
        [FEATURES.requireTokenForApprovers]: () => {
          toggleDispatchers.setRequireTokenForApproversEnabled();
        },
        [FEATURES.preferNotToSayOptionEnabled]: () => {
          toggleDispatchers.setPreferNotToSayOptionEnabled();
        },
        [FEATURES.unauthorizedCleanStateEnabled]: () => {
          toggleDispatchers.setUnauthorizedCleanStateEnabled();
        },
        [FEATURES.personasServiceEnabled]: () => {
          toggleDispatchers.setPersonasServiceEnabled();
        }
      };

      Object.keys(simpleToggles).forEach(simpleToggleKey => {
        if (toggles[simpleToggleKey]) {
          simpleToggles[simpleToggleKey].call(this);
        }
      });

      if (
        toggles[FEATURES.facebookPixel] &&
        !!publicRuntimeConfig.facebookPixelId
      ) {
        toggleDispatchers.setIsFbPixelEnabled(true);
        // load the pixel
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.innerHTML = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
          fbq('dataProcessingOptions', ['LDU'], 0, 0);
          fbq('init', '${publicRuntimeConfig.facebookPixelId}');
          fbq('track', 'PageView');
          `;
        document.head.appendChild(script);
      } else {
        toggleDispatchers.setIsFbPixelEnabled(false);
      }

      const standardToggles = getStandardToggles(toggleDispatchers);

      const promises = standardToggles.reduce(
        (prev, curr) =>
          prev.concat(setStandardToggle({ ...curr, anonymousId, attributes })),
        []
      );

      // Only get status of prove mobile auth toggle if user is coming from homepage experiment
      if (getProveMobileAuthToggleEligible()) {
        promises.push(
          setStandardToggle({
            featureName: FEATURES.proveMobileAuthEnabled,
            anonymousId,
            attributes,
            cookieName: 'proveMobileAuthEnabled',
            segmentPropertyName: 'proveMobileAuthEnabled',
            onVariationCallback: (variationKey, variables, enabled) => {
              if (
                enabled &&
                variationKey !== PROVE_MOBILE_AUTH_ENABLED.experimentOffVariant
              ) {
                toggleDispatchers.setProveMobileAuthEnabled({
                  noPhoneNumberPriceText: variables.noPhoneNumberPriceText
                });
              }
            }
          })
        );
      }

      await Promise.all(promises);

      setTogglesLoaded({ loaded: true });
    }
  } catch (error) {
    logger.error(error);
    clearTimeout(timeout);
    setFeaturesToDefault();
  }
};

const useFeatureToggles = () => {
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const {
    togglesDirty,
    appOs,
    appVersion,
    embeddedWebreg,
    shouldRefetchServersideToggles
  } = useApp();
  const { setTogglesLoaded, refetchServersideTogglesAfterLogin } =
    useAppDispatchers();
  const toggleDispatchers = useToggleDispatchers();

  const { partner } = useUser();
  const { segment } = useContext(AnalyticsContext);

  const utmSource = getUtmSource();
  const webregInAppsAttributes = getWebregInAppsAttributes(
    appOs,
    appVersion,
    embeddedWebreg
  );
  const attributes = {
    userAgent: navigator.userAgent,
    utmSource,
    ...getPartnerSource(partner),
    ...webregInAppsAttributes
  };

  useEffect(() => {
    const fetchFeatureToggles = async () => {
      const anonymousId = await getAnonymousId(segment);
      await loadFeatures({
        anonymousId,
        attributes,
        setTogglesLoaded,
        toggleDispatchers
      });
    };

    if (togglesDirty) {
      setFeaturesLoading(true);
      fetchFeatureToggles();
    }
  }, [togglesDirty]);

  useEffect(() => {
    const refetchServersideToggles = async () => {
      const anonymousId = await getAnonymousId(segment);
      refetchServersideTogglesAfterLogin({ anonymousId, attributes });
    };

    if (shouldRefetchServersideToggles) {
      refetchServersideToggles();
    }
  }, [shouldRefetchServersideToggles]);

  useEffect(() => {
    if (!togglesDirty && !shouldRefetchServersideToggles) {
      setFeaturesLoading(false);
    }
  }, [togglesDirty, shouldRefetchServersideToggles]);

  return featuresLoading;
};

export default useFeatureToggles;
